import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

const PeakHoursChart = ({ data = [] }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const isHoveringRef = useRef(false);

    // Default data if none provided
    const defaultData = [
        { time: '08:00', engagement: 20 },
        { time: '09:00', engagement: 40 },
        { time: '10:00', engagement: 50 },
        { time: '11:00', engagement: 60 },
        { time: '12:00', engagement: 70 },
        { time: '13:00', engagement: 90 },
        { time: '14:00', engagement: 60 },
        { time: '15:00', engagement: 35 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.time);
    const values = chartData.map(item => item.engagement);
    const maxValue = Math.max(...values, 10);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, 'rgba(191, 90, 242, 0.2)');
            gradient.addColorStop(1, 'rgba(191, 90, 242, 0)');

            // Calculate appropriate step size based on max value
            const stepSize = Math.ceil(maxValue / 5 / 10) * 10 || 10;
            const yMax = Math.ceil(maxValue * 1.2 / stepSize) * stepSize;

            // Custom plugin to show labels only on hover
            const hoverLabelsPlugin = {
                id: 'hoverLabelsPlugin',
                afterDatasetsDraw(chart) {
                    // Only draw labels if we're hovering
                    if (!isHoveringRef.current) return;

                    const { ctx } = chart;
                    const meta = chart.getDatasetMeta(0);

                    ctx.save();
                    meta.data.forEach((datapoint, index) => {
                        const value = chart.data.datasets[0].data[index];
                        const x = datapoint.x;
                        const y = datapoint.y;

                        // Draw the label above the point
                        ctx.fillStyle = '#666666';
                        ctx.font = '600 11px "Inter", sans-serif';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'bottom';
                        ctx.fillText(value, x, y - 8);
                    });
                    ctx.restore();
                }
            };

            chartInstance.current = new ChartJS(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Engagement',
                            data: values,
                            borderColor: '#BF5AF2',
                            backgroundColor: gradient,
                            borderWidth: 2,
                            fill: true,
                            tension: 0,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            pointHitRadius: 10,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    onHover: (event, activeElements, chart) => {
                        isHoveringRef.current = true;
                        chart.update('none');
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: false }, // Explicitly disable datalabels
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            titleColor: '#333',
                            bodyColor: '#333',
                            borderColor: '#BF5AF2',
                            borderWidth: 1,
                            padding: 10,
                            displayColors: false,
                            callbacks: {
                                label: (context) => `Engagement: ${context.raw}`
                            }
                        }
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false,
                            },
                            border: {
                                display: true,
                                color: '#e0e0e0',
                            },
                            ticks: {
                                color: '#666666',
                                font: { size: 12, family: '"Inter", sans-serif' },
                                maxRotation: 0,
                                autoSkip: false,
                            },
                        },
                        y: {
                            min: 0,
                            max: yMax,
                            ticks: {
                                stepSize: stepSize,
                                color: '#666666',
                                font: { size: 12, family: '"Inter", sans-serif' },
                            },
                            border: { display: false },
                            grid: {
                                color: '#f0f0f0',
                                drawBorder: false,
                            },
                        },
                    },
                },
                plugins: [hoverLabelsPlugin]
            });

            // Add mouse leave listener to hide labels when mouse leaves
            const canvas = chartRef.current;
            const handleMouseLeave = () => {
                isHoveringRef.current = false;
                if (chartInstance.current) {
                    chartInstance.current.update('none');
                }
            };
            canvas.addEventListener('mouseleave', handleMouseLeave);

            return () => {
                canvas.removeEventListener('mouseleave', handleMouseLeave);
                if (chartInstance.current) chartInstance.current.destroy();
            };
        }
    }, [data, labels, values, maxValue]);

    return <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default PeakHoursChart;
